/** Catálogo das imagens do satélite: busca por texto, agrupado por categoria, com miniaturas. */
import clsx from 'clsx';
import { Search, Sun } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { CATEGORY_LABELS, PRODUCTS, type GoesProduct, type ProductCategory } from '../config/products';
import { Dialog } from './primitives';
import { ProductThumbnail } from './Thumbnail';

export const CATEGORY_HELP: Record<ProductCategory, string> = {
  composicao: 'Várias bandas combinadas em cores para destacar algo específico, como fumaça, fogo ou tempestades.',
  visivel: 'Como uma foto em preto e branco: mostra a luz do Sol refletida. Só funciona de dia.',
  'infravermelho-proximo': 'Luz refletida invisível ao olho humano: vegetação, áreas queimadas e tipos de nuvem. Só de dia.',
  infravermelho: 'Mede o calor emitido: temperatura das nuvens e da superfície, de dia e de noite.',
};

const CATEGORIES = Object.keys(CATEGORY_LABELS) as ProductCategory[];

export function DayOnlyBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-yellow-400/15 px-1.5 py-0.5 text-[10px] font-medium text-yellow-300"
      title="Esta imagem só mostra informação útil durante o dia (à noite fica escura)."
    >
      <Sun className="size-3" aria-hidden />
      Só de dia
    </span>
  );
}

/** Sem acentos e em minúsculas, para a busca tolerar "temperatura"/"Temperatura"/"tempratura" não, mas "fogo"/"Fogo" sim. */
function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

/** Produtos cujo nome, sigla técnica, descrição ou categoria contêm todas as palavras da busca. */
export function filterProducts(products: readonly GoesProduct[], query: string): GoesProduct[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...products];
  return products.filter((p) => {
    const hay = fold(`${p.name} ${p.technical} ${p.description} ${CATEGORY_LABELS[p.category]} ${p.id}`);
    return words.every((w) => hay.includes(w));
  });
}

export function ProductPicker(props: {
  open: boolean;
  title: string;
  selectedId?: string;
  onSelect: (productId: string) => void;
  onClose: () => void;
}) {
  const { open, title, selectedId, onSelect, onClose } = props;
  const [query, setQuery] = useState('');
  const searchId = useId();
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);
  const matches = useMemo(() => filterProducts(PRODUCTS, query), [query]);
  const matched = new Set(matches.map((p) => p.id));
  const categories = CATEGORIES.filter((cat) => matches.some((p) => p.category === cat));

  return (
    <Dialog open={open} onClose={onClose} title={title} widthClass="max-w-3xl">
      <div className="space-y-6">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400" aria-hidden />
          <input
            id={searchId}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar: fumaça, fogo, banda 13, vapor d’água…"
            aria-label="Buscar imagem do satélite"
            autoComplete="off"
            className="h-10 w-full rounded-xl border border-ink-600 bg-ink-800 pr-3 pl-9 text-sm text-ink-100 placeholder:text-ink-400 focus:border-mata-400 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400"
          />
        </div>
        {categories.length === 0 && (
          <p className="text-sm text-ink-300" role="status">
            Nenhuma imagem combina com “{query}”. Tente outra palavra, como “nuvens”, “fogo” ou “banda”.
          </p>
        )}
        {categories.map((cat) => (
          <section key={cat} aria-labelledby={`cat-${cat}`}>
            <h3 id={`cat-${cat}`} className="text-sm font-semibold text-white">
              {CATEGORY_LABELS[cat]}
            </h3>
            <p className="mt-0.5 mb-3 text-xs text-ink-300">{CATEGORY_HELP[cat]}</p>
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {PRODUCTS.filter((p) => p.category === cat && matched.has(p.id)).map((p) => {
                const selected = p.id === selectedId;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(p.id)}
                      aria-pressed={selected}
                      className={clsx(
                        'flex w-full gap-3 rounded-xl border p-2 text-left transition-colors',
                        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400',
                        selected
                          ? 'border-mata-400 bg-mata-500/10'
                          : 'border-ink-700 bg-ink-850/60 hover:border-ink-500 hover:bg-ink-800',
                      )}
                    >
                      <ProductThumbnail productId={p.id} lazy width={96} height={58} className="h-[58px] w-24 shrink-0 rounded-md" />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-medium text-ink-100">{p.name}</span>
                          {p.dayOnly && <DayOnlyBadge />}
                        </span>
                        <span className="block text-[11px] text-ink-300">{p.technical}</span>
                        <span className="mt-1 line-clamp-2 block text-xs leading-snug text-ink-300">{p.description}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
